import { useSortable } from "@dnd-kit/sortable";
import {
  faArrowUpArrowDown,
  faPencil,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Tooltip, Typography } from "@mui/material";
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import useUpdateReleaseTrack from "../../../api/releases/hooks/useUpdateReleaseTrack";
import { SUPPLY_CHAIN_ACTIVE } from "../../../constants/generated";
import { TRACKING_EVENTS_NAME } from "../../../constants/trackingEvents";
import { useMediaQueryBreakpoint } from "../../../hooks/useMediaQuery";
import { ReleaseStatus, ReleaseTrack } from "../../../store/models/release";
import { PennyDollarFormatter } from "../../../store/utils/formatUtils";
import { emitAnalyticsTrackingEvent } from "../../../utils/analyticsUtils";
import { Button } from "../../core-ui/components/Button/Button";
import { ButtonVariant } from "../../core-ui/components/Button/utils";
import { PopConfirm } from "../../core-ui/components/PopConfirm/PopConfirm";
import { AddReleaseTrackToCartButton } from "../AddReleaseTrackToCartButton/AddReleaseTrackToCartButton";
import {
  PlayButton,
  RowDraggerButton,
} from "../ProjectOverviewTable/NewProjectsTable.styles";
import { ReleaseTrackPlayPauseButton } from "../ReleasePlayPauseButton/ReleasePlayPauseButton";
import { useShowEditReleaseTrackSidePanel } from "../ReleaseTrack/EditReleaseTrackSidePanel/useShowEditReleaseTrackSidePanel";
import { WrappedReleaseTrackStatus } from "../ReleaseTrack/WrappedReleaseTrackStatus";
import { ReleaseTrackOptions } from "./ReleaseTrackOptions";
import { ToolTipTextArea } from "../ToolTipTextArea/ToolTipTextArea";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";

export enum ReleaseTrackTableRowId {
  ReorderTab = "reorder-tab",
  Title = "title",
  PlaySection = "play_section",
  Price = "price",
  // PlayCount = "play_count",
  DropDown = "drop_down",
  CartButton = "cart_button",
  ActionButton = "action_button",
  ArtistTitle = "artist_title",
  ReleaseTitle = "release_title",
  Status = "status",
}

export const releaseTrackColumnOrderConsumer = [
  ReleaseTrackTableRowId.Title,
  ReleaseTrackTableRowId.PlaySection,
  ReleaseTrackTableRowId.Price,
  ReleaseTrackTableRowId.CartButton,
  ReleaseTrackTableRowId.DropDown,
];

export const releaseTrackColumnOrderArtist = [
  ReleaseTrackTableRowId.ReorderTab,
  ReleaseTrackTableRowId.Title,
  ReleaseTrackTableRowId.ArtistTitle,
  ReleaseTrackTableRowId.Price,
  ReleaseTrackTableRowId.Status,
  ReleaseTrackTableRowId.PlaySection,
  ReleaseTrackTableRowId.ActionButton,
];

export interface ExtendedCellContext<TData, TValue>
  extends CellContext<TData, TValue> {
  unitPrice?: string | null;
  currency?: string;
  selectedProductId?: number;
  isArtistOnRelease?: boolean;
  openFeedbackModal?: () => void;
  isCreateMode?: boolean;
  isDesktop?: boolean;
  releaseIsStreamEnabled?: boolean;
}

const RowDragHandleCell = ({
  rowId,
  isPending,
}: {
  rowId: number;
  isPending: boolean;
}) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });

  return (
    <RowDraggerButton disabled={isPending} {...attributes} {...listeners}>
      <FontAwesomeIcon icon={faArrowUpArrowDown} size="sm" />
    </RowDraggerButton>
  );
};

const columnHelper = createColumnHelper<ReleaseTrack>();

const EditDetailsButton = ({
  releaseTrackId,
  isCreateMode,
}: {
  releaseTrackId: number;
  isCreateMode: boolean;
}) => {
  const { isMobile } = useMediaQueryBreakpoint();
  const { mutateAsync: updateReleaseTrack, isPending: isSaving } =
    useUpdateReleaseTrack(releaseTrackId);

  const deleteTrack = async () => {
    await updateReleaseTrack({ deleted_at: true });
  };

  const showEditReleaseTrackSidePanel = useShowEditReleaseTrackSidePanel();
  return (
    <Box display="flex" gap={1}>
      <Button
        style={{
          minWidth: "fit-content",
          whiteSpace: "nowrap",
        }}
        variant={
          isMobile
            ? ButtonVariant.SUCCESS
            : ButtonVariant.BLACK_OUTLINED_BUTTON_BLACK_HOVER
        }
        onClick={() => showEditReleaseTrackSidePanel(releaseTrackId)}
      >
        {isMobile ? <FontAwesomeIcon icon={faPencil} size="sm" /> : "Edit"}
      </Button>
      {isCreateMode && (
        <PopConfirm
          onConfirm={() => {
            emitAnalyticsTrackingEvent(
              TRACKING_EVENTS_NAME.ARTIST_CLICKED_DELETE_RELEASE_TRACK,
              { release_track_id: releaseTrackId },
            );
            deleteTrack();
          }}
          title="Are you sure?"
          description="You are about to remove this track."
        >
          <Button
            style={{
              minWidth: "fit-content",
              whiteSpace: "nowrap",
            }}
            variant={ButtonVariant.DELETE}
            loading={isSaving}
            loaderWidth={20}
          >
            <FontAwesomeIcon icon={faTrash} size="sm" />
          </Button>
        </PopConfirm>
      )}
    </Box>
  );
};

export const releaseTrackColumns = (
  releaseId?: number,
): ColumnDef<ReleaseTrack>[] => [
  columnHelper.display({
    size: 30,
    id: ReleaseTrackTableRowId.ReorderTab,
    header: () => null,
    cell: (cell: ExtendedCellContext<ReleaseTrack, unknown>) => {
      const { isArtistOnRelease } = cell;
      const { original } = cell.row;
      if (!isArtistOnRelease) {
        return null;
      }
      return (
        <RowDragHandleCell
          rowId={original.id}
          isPending={cell.table.options.meta?.reorderLoading || false}
        />
      );
    },
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.Title,
    header: "Title",
    cell: ({ row, row: { original } }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1.5,
          width: "100%",
        }}
      >
        <Typography>{row.index + 1}.</Typography>
        <Tooltip
          title={original.title}
          arrow
          enterDelay={500}
          disableHoverListener={original.title.length < 15}
        >
          <Typography
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {original.title}
          </Typography>
        </Tooltip>
      </Box>
    ),
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.ArtistTitle,
    header: "Artist",
    size: 120,
    cell: ({ row: { original } }) => (
      <Typography
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {original.release.artist_display_name}
      </Typography>
    ),
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.Price,
    size: 40,
    header: "Price",
    cell: (row: ExtendedCellContext<ReleaseTrack, unknown>) => {
      const { unitPrice, currency } = row;
      const convertedUnitPrice = parseFloat(unitPrice ?? "0");
      if (!convertedUnitPrice) {
        return <Typography>Album only</Typography>;
      }
      return (
        <Box>{PennyDollarFormatter(currency).format(convertedUnitPrice)}</Box>
      );
    },
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.Status,
    size: 120,
    header: "Status",
    cell: (row: ExtendedCellContext<ReleaseTrack, unknown>) => {
      const isUploadVerified =
        SUPPLY_CHAIN_ACTIVE && row.releaseIsStreamEnabled
          ? Boolean(row.row.original.supply_chain_file_id)
          : Boolean(row.row.original.file_version_id);
      return (
        <Box minWidth={"120px"}>
          <WrappedReleaseTrackStatus
            releaseTrackId={row.row.original.id}
            isUploadVerified={isUploadVerified}
            isFree={row.row.original.release.status === ReleaseStatus.FOR_FREE}
          />
        </Box>
      );
    },
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.PlaySection,
    size: 150,
    cell: (cell: ExtendedCellContext<ReleaseTrack, unknown>) => {
      const { isCreateMode, releaseIsStreamEnabled } = cell;
      const { original } = cell.row;
      const isUploadVerified =
        SUPPLY_CHAIN_ACTIVE && releaseIsStreamEnabled
          ? Boolean(original.supply_chain_file_id)
          : Boolean(original.file_version_id);
      if (!original.file_version_id) {
        return (
          <ToolTipTextArea text="Purchase to start listening, or sign in to an account that previously purchased this track">
            <PlayButton disabled>
              <FontAwesomeIcon icon={faPlay} />
            </PlayButton>
          </ToolTipTextArea>
        );
      }
      return (
        <Box
          sx={{
            width: "100%",
            flex: "1 1 auto",
            minHeight: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {isUploadVerified ? (
            <ReleaseTrackPlayPauseButton
              trackId={original.id}
              releaseId={original.release.id}
              // FeaturedTrackPlayer does not accept null
              // We only want to show filenames when it's in the create flow
              {...(isCreateMode && {
                fileName: original.file_name ?? undefined,
              })}
            />
          ) : (
            // Not showing waveform for failed tracks
            <></>
          )}
        </Box>
      );
    },
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.ActionButton,
    size: 120,
    header: () => "Action",
    cell: (cell: ExtendedCellContext<ReleaseTrack, unknown>) => {
      return (
        <EditDetailsButton
          releaseTrackId={cell.row.original.id}
          isCreateMode={cell.isCreateMode || false}
        />
      );
    },
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.CartButton,
    size: 40,
    header: () => null,
    cell: (row: ExtendedCellContext<ReleaseTrack, unknown>) => {
      const { unitPrice, selectedProductId } = row;
      const {
        id: releaseTrackId,
        release: { id: releaseId },
      } = row.row.original;
      if (!unitPrice) {
        return null;
      }
      return (
        <AddReleaseTrackToCartButton
          releaseTrackId={releaseTrackId}
          unitPrice={unitPrice ?? null}
          productToGetPriceFromId={selectedProductId ?? 0}
          releaseId={releaseId}
        />
      );
    },
  }),
  columnHelper.display({
    id: ReleaseTrackTableRowId.DropDown,
    size: 40,
    header: () => null,
    cell: (cell: ExtendedCellContext<ReleaseTrack, unknown>) => {
      const openFeedbackModal = cell.openFeedbackModal;
      if (!openFeedbackModal) {
        return null;
      }
      const { original } = cell.row;
      if (!original) {
        return null;
      }
      return (
        <ReleaseTrackOptions
          releaseId={releaseId}
          openFeedbackModal={openFeedbackModal}
          releaseTrack={original}
        />
      );
    },
  }),
];
